import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import iconThumpUp from '../images/fa-thumbs-up.svg';
import iconThumpUpYellow from '../images/fa-thumbs-up-yellow.svg';
import iconThumpUpGreen from '../images/fa-thumbs-up-green.svg';
import iconThumbDown from '../images/fa-thumbs-down.svg';
import iconThumbDownYellow from '../images/fa-thumbs-down-yellow.svg';
import iconThumbDownRed from '../images/fa-thumbs-down-red.svg';
import iconSearch from '../images/fa-search.svg';
import iconTopicChange from '../images/fa-circle-info-thin.svg';
import { FeedbackDetail, FeedbackType } from '../types/Feedback';
import { ConversationContext } from '../contexts/ConversationContext';
import { BotResponseData } from '../types/MessageData';
import { FeedbackFormNegative } from './FeedbackFormNegative';
import { backendProxy } from '../hooks/backendProxy';
import { TopicFilter } from './TopicFilter';
import { FeedbackFormPositive } from './FeedbackFormPositive';

const FeedbackDiv = styled.div`
    position: relative; display: flex; align-items: center;
    min-height: 40px; margin-top: 20px; font-size: 12px; line-height: 14.4px;
    .legend {opacity: 0.6;}
    .legend:not(:first-child) {margin-left: 20px;}
    .legend.desktop-visible {
        @media (max-width: 768px) {
            display: none;
        }
    }
    a {
        position: relative; color: #000; font-size: 20px; padding: 10px;
        &.search {
            display: flex; align-items: center; padding: 0; text-decoration: none; color: #000;
            .legend {
                display: inline-block; font-size: 12px; line-height: 14.4px; margin-left: 20px;
                @media (max-width: 520px) {margin-left: 10px;}
            }
            .legend.uppersmallmobile {
                display: block;
                @media (max-width: 500px) {display: none;}
            }
            .legend.smallmobile {
                display: none;
                @media (max-width: 500px) {display: block;}
            }
            img {padding: 10px;}
            &:hover {
                padding: 0;
                img {width: 24px; padding: 8px;}
            }
        }
        .tooltip {
            visibility: hidden;
            width: 92px;
            background-color: #008334;
            color: #fff;
            text-align: center;
            padding: 5px;
            border-radius: 4px;
            position: absolute;
            bottom: 42px;
            left: 50%;
            transform: translateX(-50%);
            opacity: 0;
            transition: opacity 0.3s;
            box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
            font-size: 12px; line-height: 14px; font-weight: 600;
            @media (max-width: 768px) {display: none; visibility: hidden;}
            &::after {
                content: "";
                position: absolute;
                top: 100%;
                left: 50%;
                margin-left: -5px;
                border-width: 5px;
                border-style: solid;
                border-color: #008334 transparent transparent transparent;
                @media (max-width: 768px) {display: none; visibility: hidden;}
            }
        }
        img {width: 20px;}
        &:hover {
            padding: 8px;
            .tooltip {visibility: visible; opacity: 1;}
            img {width: 24px;}
        }
        &.active {
            padding: 8px; /*pointer-events: none;*/
            img {width: 24px;}
        }
        &.initState {
            padding: 8px; pointer-events: none;
            img {width: 24px;}
        }
        &.desktop-visible {
            @media (max-width: 768px) {
                display: none;
            }
        }
    }
`

/* TODO Styling:
 - UI sollte unnutzbar / deaktiviert sein, sofern das Feedback Form angezeigt wird.
*/


type FeedbackProps = {
    message: BotResponseData;
}
export const Feedback: React.FC<FeedbackProps> = ({ message }) => {
    const { updateMessage, clientId, serializedConversationHistory, messages} = useContext(ConversationContext);
    const [feedbackInProgress, setFeedbackInProgress] = useState<FeedbackType | undefined>(undefined);
    const [topicFilterVisible, setTopicFilterVisible] = useState(false);
    const [error, setError] = useState<string>();

    
    const cancelFeedback = async() => {
        await backendProxy.trackEvent('cancel-feedback', clientId, { type: feedbackInProgress || '', messageId: message.messageId });
        setFeedbackInProgress(undefined);
    }

    const sendFeedback = async (details: FeedbackDetail) => {
        try {
            await backendProxy.postFeedback(message.messageId, serializedConversationHistory, details);
            message.feedback = details.type;
            updateMessage(message);
        } catch (e: any) {
            setError(e.message || JSON.stringify(e));
        } finally {
            setFeedbackInProgress(undefined);
        }
    }
    const toggleFeedback = async (type: FeedbackType) => {
        setError(undefined);
        if (message.feedback === type) {
            await backendProxy.trackEvent('remove-feedback', clientId, { type, messageId: message.messageId })
            await sendFeedback({ type: 'none' });
        } else {
            await backendProxy.trackEvent('start-feedback', clientId, { type, messageId: message.messageId })
            setFeedbackInProgress(type);
        }
    }

    const callSearch = async () => {
        let lastUserQuestion: string = '';
        for (let i=0; i<messages.length; i++) {
            const msg = messages[i];
            if (msg.role === 'user') {
                lastUserQuestion = msg.preprocessingResult?.contextualized_input || msg.content;
            }
            if (msg.messageId === message.messageId) {
                break;
            }
        }
        await backendProxy.trackEvent('open-search', clientId, { lastUserQuestion, feedback: message.feedback, messageId: message.messageId});
        window.open(`https://www.sg.ch/tools/suchen.html#stq=${lastUserQuestion}&stp=1`)
    }

    const thumbUpClass = feedbackInProgress === 'positive' ? 'initState' : (message.feedback === 'positive' ? 'active' : undefined);
    const thumbDownClass = feedbackInProgress === 'negative' ? 'initState' : (message.feedback === 'negative' ? 'active' : undefined);

    const thumbUpImage = feedbackInProgress === 'positive' ? iconThumpUpYellow : (message.feedback === 'positive' ? iconThumpUpGreen : iconThumpUp);
    const thumbDownImage = feedbackInProgress === 'negative' ? iconThumbDownYellow : (message.feedback === 'negative' ? iconThumbDownRed : iconThumbDown);

    const thumbUpTooltip = (message.feedback === 'positive' ? "Positive Bewertung entfernen" : "Antwort positiv bewerten");
    const thumbDownTooltip = (message.feedback === 'negative' ? "Negative Bewertung entfernen" : "Antwort negativ bewerten");

    return (
        <>
            {error && <div>Fehler: {error} </div>}
            <FeedbackDiv>
                {message.completed &&
                    <><span className="legend">Antwort<br />bewerten</span>
                        <a href="/" onClick={e => {toggleFeedback('positive'); e.preventDefault(); }} className={thumbUpClass} title="Daumen hoch">
                            <span className="tooltip">{thumbUpTooltip}</span>
                            <img src={thumbUpImage} alt="Daumen hoch" />
                        </a>
                        <a href="/" onClick={e => {toggleFeedback('negative'); e.preventDefault(); }} className={thumbDownClass} title="Daumen runter">
                            <span className="tooltip">{thumbDownTooltip}</span>
                            <img src={thumbDownImage} alt="Daumen runter" />
                        </a>
                        { feedbackInProgress === 'positive' && 
                            <FeedbackFormPositive type={feedbackInProgress} topic={message.subTopic ? message.subTopic: message.mainTopic} onSave={sendFeedback} onCancel={cancelFeedback}/>
                        }
                        { feedbackInProgress === 'negative' && 
                            <FeedbackFormNegative type={feedbackInProgress} topic={message.subTopic ? message.subTopic: message.mainTopic} onSave={sendFeedback} onCancel={cancelFeedback}/>
                        }
                        <a href="/" onClick={e => { callSearch(); e.preventDefault(); }} title="Konventionelle Suche" className="search">
                            <span className="legend uppersmallmobile">Konventionelle<br />Suche</span>
                            <span className="legend smallmobile">Suche<br />sg.ch</span>
                            <span className="tooltip">Frage in konventioneller Suche stellen</span>
                            <img src={iconSearch} alt="Konventionelle Suche" />
                        </a>
                        { message.completed && (message.classification === 'fully-answered' || message.classification === 'partly-answered' || message.classification === 'fixed-answer') &&
                        <a href="/" onClick={e => { setTopicFilterVisible(!topicFilterVisible); e.preventDefault(); }} title="Thema verfehlt?" className="search">
                            <span className="legend uppersmallmobile">{message.mainTopic}<br/>Thema verfehlt?</span>
                            <span className="legend smallmobile">Thema<br/>verfehlt?</span>
                            <span className="tooltip">Resultate aus anderem Themenbereich</span>
                            <img src={iconTopicChange} alt="Thema verfehlt?" />
                            
                            { topicFilterVisible && 
                                <TopicFilter message={message} onCancel={()=> setTopicFilterVisible(false)}/>
                            }
                        </a>
                        }
                    </>
                }
            </FeedbackDiv>
        </>
    )
}